import type { LatLngExpression } from "leaflet";
import { ConnectProto, Point } from "@racoon/api";
import type { TravelPathResponse } from "~/shared/api/truck/truck.grpc";
import type { RouteWaypointsResponse } from "~/shared/api/cost-provider/cost-provider.api";
import { decodeRouteWaypoints } from "./route-decoder";
import { normalizeGpsData } from "./gps-normalization";
import type { MapObject } from "../../ui/map-view";
import { isValidRoutePoint, sortJobsByStep, toDateLike } from "@racoon/lib";
import { photoPointsForJobs, type PhotoPoint } from "./photo-points";

const parseDate = toDateLike;

export type RouteSegment = {
  path: LatLngExpression[];
  color: string;
  dashArray?: string; // "2, 6" for dashed lines
  weight: number;
  opacity: number;
  offsetMeters?: number; // Offset from center line to prevent overlaps
  /** Debug info: logic branch that created this segment */
  status?: string;
};

export type RouteVisualization = {
  segments: RouteSegment[];
  locationJobStatuses: Map<number, ConnectProto.JobStatus>; // Map of location ID to job status
  photoPoints: PhotoPoint[];
};

type CalculateRouteWaypointsFn = (params: { waypoints: Point[] }) => {
  unwrap: () => Promise<RouteWaypointsResponse>;
};

/**
 * Builds complete route visualization based on route status
 * Handles three scenarios:
 * 1. Created / not-approved routes: All segments are future (dashed blue)
 * 2. In-progress routes: Mix of historical GPS (black), current (blue solid), and future (blue dashed)
 * 3. Returning/finalized routes: Historical GPS (black) + optional return path (blue solid)
 */
export async function buildRouteVisualization(
  route: ConnectProto.Route,
  truck: MapObject,
  calculateRouteWaypoints: CalculateRouteWaypointsFn,
  travelPathData?: TravelPathResponse,
): Promise<RouteVisualization> {
  const segments: RouteSegment[] = [];
  const locationJobStatuses = new Map<number, ConnectProto.JobStatus>();

  // The segments below slice this as visit order, and ListRoutes preloads jobs
  // with no ORDER BY.
  const jobs = sortJobsByStep(route.jobs ?? []);
  const status = Number(route.status);
  const isCreatedRoute =
    status === ConnectProto.RouteStatus.CREATED || status === ConnectProto.RouteStatus.NOT_APPROVED;
  const isInProgress = status === ConnectProto.RouteStatus.IN_PROGRESS;
  const isReturning = status === ConnectProto.RouteStatus.RETURNING;
  const isCompleted = status === ConnectProto.RouteStatus.COMPLETED;
  const isCancelled = status === ConnectProto.RouteStatus.CANCELLED;
  const isForceCompleted = status === ConnectProto.RouteStatus.FORCE_COMPLETED;

  if (isCreatedRoute) {
    // All segments are future (dashed blue)
    await buildFutureSegments(
      truck,
      jobs,
      route,
      calculateRouteWaypoints,
      segments,
      locationJobStatuses,
    );
  } else if (isInProgress) {
    // Mix of completed, current, and future
    await buildProgressSegments(
      truck,
      jobs,
      route,
      calculateRouteWaypoints,
      travelPathData,
      segments,
      locationJobStatuses,
    );
  } else if (isReturning || isCompleted || isCancelled || isForceCompleted) {
    // Finalized route states use historical track + optional return path
    await buildReturningSegments(
      truck,
      route,
      calculateRouteWaypoints,
      travelPathData,
      segments,
      locationJobStatuses,
    );
  }

  return { segments, locationJobStatuses, photoPoints: photoPointsForJobs(jobs) };
}

/**
 * Builds all route segments as dashed blue lines for created routes
 * Shows the planned route before the truck starts
 */
async function buildFutureSegments(
  truck: MapObject,
  jobs: ConnectProto.Job[],
  route: ConnectProto.Route,
  calculateRouteWaypoints: CalculateRouteWaypointsFn,
  segments: RouteSegment[],
  locationJobStatuses: Map<number, ConnectProto.JobStatus>,
) {
  const waypoints: Point[] = [{ lat: truck.lat, lon: truck.lon, date_time: new Date() }];

  // Add all job locations
  jobs.forEach((job) => {
    const pickupLoc = job.pickupLocation;
    if (isValidRoutePoint(pickupLoc)) {
      waypoints.push({
        lat: pickupLoc.lat,
        lon: pickupLoc.lon,
        date_time: parseDate(job.planStartAt) || new Date(),
      });

      locationJobStatuses.set(Number(job.pickupLocationId), job.status);
    }
  });

  // Add return destination
  if (isValidRoutePoint({ lat: route.destinationLat, lon: route.destinationLon })) {
    waypoints.push({
      lat: route.destinationLat,
      lon: route.destinationLon,
      date_time: parseDate(route.planEndAt) || new Date(),
    });
  }

  // Call route_waypoints API
  try {
    const response = await calculateRouteWaypoints({ waypoints }).unwrap();
    const path = decodeRouteWaypoints(response);

    if (path.length > 0) {
      segments.push({
        path,
        color: "#007AFF",
        dashArray: "2, 6",
        weight: 4,
        opacity: 0.8,
      });
    }
  } catch (error) {
    console.warn("Failed to calculate future route segments:", error);
  }
}

/**
 * Builds segments for in-progress routes
 * Combines historical GPS data with future predictions
 */
async function buildProgressSegments(
  truck: MapObject,
  jobs: ConnectProto.Job[],
  route: ConnectProto.Route,
  calculateRouteWaypoints: CalculateRouteWaypointsFn,
  travelPathData: TravelPathResponse | undefined,
  segments: RouteSegment[],
  locationJobStatuses: Map<number, ConnectProto.JobStatus>,
) {
  // 1. Add historical GPS track for completed portion (black solid)
  if (travelPathData?.geometry?.coordinates) {
    // Prepare data for minimal normalization (hard changes only)
    const rawPoints = travelPathData.geometry.coordinates.map((coord, index) => ({
      lat: coord[1],
      lon: coord[0],
      timestamp: travelPathData.properties.timestamps?.[index] || 0,
    }));

    const normalizedPoints = normalizeGpsData(rawPoints);

    const historicalPath = normalizedPoints.map((p) => [p.lat, p.lon] as LatLngExpression);

    if (historicalPath.length > 0) {
      segments.push({
        path: historicalPath,
        color: "#000000",
        weight: 4,
        opacity: 1,
      });
    }
  }

  // Determine normalized current truck position
  // Use the last point of the historical track if available, to ensure smooth connection
  // Otherwise use raw truck position
  const lastHistoricalPoint =
    segments.length > 0
      ? (segments[segments.length - 1].path.slice(-1)[0] as LatLngExpression)
      : null;

  // Check if we have a valid historical point (array of 2 numbers)
  const normalizedTruckPosition =
    lastHistoricalPoint && Array.isArray(lastHistoricalPoint)
      ? { lat: lastHistoricalPoint[0], lon: lastHistoricalPoint[1] }
      : { lat: truck.lat, lon: truck.lon };

  // 2. Find current job and future jobs
  const currentJobIndex = jobs.findIndex((j) => {
    const status = Number(j.status);
    return (
      status === ConnectProto.JobStatus.ENROUTE || status === ConnectProto.JobStatus.ON_SERVICE
    );
  });

  const completedJobs = currentJobIndex >= 0 ? jobs.slice(0, currentJobIndex) : jobs;
  const currentJob = currentJobIndex >= 0 ? jobs[currentJobIndex] : null;
  const futureJobs = currentJobIndex >= 0 ? jobs.slice(currentJobIndex + 1) : [];

  // 3. Add completed job markers (gray with black border)
  completedJobs.forEach((job) => {
    const pickupLoc = job.pickupLocation;
    if (isValidRoutePoint(pickupLoc)) {
      locationJobStatuses.set(Number(job.pickupLocationId), ConnectProto.JobStatus.COMPLETED);
    }
  });

  // Execute all route calculations in parallel
  // We prepare promises for current, future, and return paths
  const currentPickupLoc = currentJob?.pickupLocation ?? null;
  const currentRoutePromise = isValidRoutePoint(currentPickupLoc)
    ? calculateRouteWaypoints({
        waypoints: [
          {
            lat: normalizedTruckPosition.lat,
            lon: normalizedTruckPosition.lon,
            date_time: new Date(),
          },
          {
            lat: currentPickupLoc.lat,
            lon: currentPickupLoc.lon,
            date_time: parseDate(currentJob?.planStartAt) || new Date(),
          },
        ],
      })
        .unwrap()
        .then((response) => ({ type: "current", path: decodeRouteWaypoints(response) }))
        .catch((e) => {
          console.warn("Failed to calculate current job route:", e);
          return null;
        })
    : Promise.resolve(null);

  // Prepare future waypoints
  let futureRoutePromise: Promise<{ type: string; path: LatLngExpression[] } | null> =
    Promise.resolve(null);
  if (futureJobs.length > 0) {
    const futureWaypoints: Point[] = [];
    // Start from current job or truck position
    if (isValidRoutePoint(currentPickupLoc)) {
      futureWaypoints.push({
        lat: currentPickupLoc.lat,
        lon: currentPickupLoc.lon,
        date_time: parseDate(currentJob?.planEndAt) || new Date(),
      });
    } else {
      futureWaypoints.push({
        lat: normalizedTruckPosition.lat,
        lon: normalizedTruckPosition.lon,
        date_time: new Date(),
      });
    }

    futureJobs.forEach((job) => {
      const pickupLoc = job.pickupLocation;
      if (isValidRoutePoint(pickupLoc)) {
        futureWaypoints.push({
          lat: pickupLoc.lat,
          lon: pickupLoc.lon,
          date_time: parseDate(job.planStartAt) || new Date(),
        });
        locationJobStatuses.set(Number(job.pickupLocationId), ConnectProto.JobStatus.ASSIGNED);
      }
    });

    if (isValidRoutePoint({ lat: route.destinationLat, lon: route.destinationLon })) {
      futureWaypoints.push({
        lat: route.destinationLat,
        lon: route.destinationLon,
        date_time: parseDate(route.planEndAt) || new Date(),
      });
    }

    futureRoutePromise = calculateRouteWaypoints({ waypoints: futureWaypoints })
      .unwrap()
      .then((response) => ({ type: "future", path: decodeRouteWaypoints(response) }))
      .catch((e) => {
        console.warn("Failed to calculate future route segments:", e);
        return null;
      });
  }

  // Prepare return waypoints (only if no future jobs)
  let returnRoutePromise: Promise<{ type: string; path: LatLngExpression[] } | null> =
    Promise.resolve(null);
  if (
    futureJobs.length === 0 &&
    isValidRoutePoint({ lat: route.destinationLat, lon: route.destinationLon })
  ) {
    const returnWaypoints: Point[] = [];
    if (isValidRoutePoint(currentPickupLoc)) {
      returnWaypoints.push({
        lat: currentPickupLoc.lat,
        lon: currentPickupLoc.lon,
        date_time: parseDate(currentJob?.planEndAt) || new Date(),
      });
    } else {
      returnWaypoints.push({
        lat: normalizedTruckPosition.lat,
        lon: normalizedTruckPosition.lon,
        date_time: new Date(),
      });
    }
    returnWaypoints.push({
      lat: route.destinationLat,
      lon: route.destinationLon,
      date_time: parseDate(route.planEndAt) || new Date(),
    });

    returnRoutePromise = calculateRouteWaypoints({ waypoints: returnWaypoints })
      .unwrap()
      .then((response) => ({ type: "return", path: decodeRouteWaypoints(response) }))
      .catch((e) => {
        console.warn("Failed to calculate return path:", e);
        return null;
      });
  }

  // Await all calculations
  const [currentResult, futureResult, returnResult] = await Promise.all([
    currentRoutePromise,
    futureRoutePromise,
    returnRoutePromise as Promise<{ type: string; path: LatLngExpression[] } | null>,
  ]);

  // Add segments in correct order
  if (currentResult?.path && currentResult.path.length > 0) {
    segments.push({
      path: currentResult.path,
      color: "#007AFF",
      weight: 4,
      opacity: 1,
    });
    if (currentJob) {
      locationJobStatuses.set(Number(currentJob.pickupLocationId), currentJob.status);
    }
  }

  if (futureResult?.path && futureResult.path.length > 0) {
    segments.push({
      path: futureResult.path,
      color: "#007AFF",
      dashArray: "2, 6",
      weight: 4,
      opacity: 0.8,
    });
  }

  if (returnResult?.path && returnResult.path.length > 0) {
    segments.push({
      path: returnResult.path,
      color: "#007AFF",
      dashArray: "2, 6",
      weight: 4,
      opacity: 0.8,
    });
  }
}

/**
 * Builds segments for returning routes
 * Shows historical GPS track and return path to depot
 */
async function buildReturningSegments(
  truck: MapObject,
  route: ConnectProto.Route,
  calculateRouteWaypoints: CalculateRouteWaypointsFn,
  travelPathData: TravelPathResponse | undefined,
  segments: RouteSegment[],
  locationJobStatuses: Map<number, ConnectProto.JobStatus>,
) {
  // 1. Add historical GPS track (black solid)
  if (travelPathData?.geometry?.coordinates) {
    // Prepare data for minimal normalization (hard changes only)
    const rawPoints = travelPathData.geometry.coordinates.map((coord, index) => ({
      lat: coord[1],
      lon: coord[0],
      timestamp: travelPathData.properties.timestamps?.[index] || 0,
    }));

    const normalizedPoints = normalizeGpsData(rawPoints);

    const historicalPath = normalizedPoints.map((p) => [p.lat, p.lon] as LatLngExpression);

    if (historicalPath.length > 0) {
      segments.push({
        path: historicalPath,
        color: "#000000",
        weight: 4,
        opacity: 1,
      });
    }
  }

  // 2. Preserve actual job statuses for marker coloring in finalized routes
  const jobs = route.jobs ?? [];
  jobs.forEach((job) => {
    const pickupLoc = job.pickupLocation;
    if (isValidRoutePoint(pickupLoc)) {
      locationJobStatuses.set(Number(job.pickupLocationId), job.status);
    }
  });

  // 3. Add return path from current truck position (or end of historical track) to destination
  if (
    isValidRoutePoint({ lat: route.destinationLat, lon: route.destinationLon }) &&
    Number(route.status) === ConnectProto.RouteStatus.RETURNING
  ) {
    // Determine normalized current truck position
    const lastHistoricalPoint =
      segments.length > 0
        ? (segments[segments.length - 1].path.slice(-1)[0] as LatLngExpression)
        : null;

    const normalizedTruckPosition =
      lastHistoricalPoint && Array.isArray(lastHistoricalPoint)
        ? { lat: lastHistoricalPoint[0], lon: lastHistoricalPoint[1] }
        : { lat: truck.lat, lon: truck.lon };

    const returnWaypoints: Point[] = [
      { lat: normalizedTruckPosition.lat, lon: normalizedTruckPosition.lon, date_time: new Date() },
      {
        lat: route.destinationLat,
        lon: route.destinationLon,
        date_time: parseDate(route.planEndAt) || new Date(),
      },
    ];

    try {
      const returnResponse = await calculateRouteWaypoints({
        waypoints: returnWaypoints,
      }).unwrap();
      const returnPath = decodeRouteWaypoints(returnResponse);

      if (returnPath.length > 0) {
        segments.push({
          path: returnPath,
          color: "#007AFF",
          weight: 4,
          opacity: 0.8,
          status: "return-path",
        });
      }
    } catch (error) {
      console.warn("Failed to calculate return path in returning state:", error);
    }
  }
}
